import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@repo/ui";
import { Calendar, Clock, User } from "lucide-react";
import {
  formatDateTime,
  formatDuration,
  buildFamillesAutodiag,
  categorieColors,
  categorieLabels,
  type ReferentielCompetences,
  type AutodiagReponse,
} from "@repo/utils";

interface AutodiagDonnees {
  type: "form";
  template: "autodiag";
  reponses: Record<string, AutodiagReponse & { label: string }>;
}

export type ReferentielAutodiag = ReferentielCompetences;

interface CandidatInfo {
  prenom: string;
  nom: string;
  email: string;
  dateDebut?: number;
  dateFin?: number;
  tempsPasse?: number;
}

interface AutodiagResultsProps {
  donnees: AutodiagDonnees;
  referentiel: ReferentielAutodiag;
  candidatInfo: CandidatInfo;
}


export function AutodiagResults({
  donnees,
  referentiel,
  candidatInfo,
}: AutodiagResultsProps) {
  if (donnees.type !== "form" || donnees.template !== "autodiag") {
    return <div>Type de données incorrect</div>;
  }

  const familles = buildFamillesAutodiag(donnees.reponses, referentiel);

  return (
    <div>
      {/* Header */}
      <div className="mb-6 p-4 md:p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">Autodiagnostic</h1>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <User className="h-4 w-4" />
              {candidatInfo.prenom} {candidatInfo.nom} —{" "}
              {candidatInfo.email}
            </div>
          </div>
          <Badge variant="success">Terminée</Badge>
        </div>
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {candidatInfo.tempsPasse !== undefined && (
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Temps passé</span>
                <p className="font-medium">
                  {formatDuration(candidatInfo.tempsPasse)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateDebut && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de début</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateDebut)}
                </p>
              </div>
            </div>
          )}
          {candidatInfo.dateFin && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <div>
                <span className="text-muted-foreground">Date de fin</span>
                <p className="font-medium">
                  {formatDateTime(candidatInfo.dateFin)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card Résultats bruts */}
      <Card className="mb-6 w-full overflow-hidden">
        <CardHeader>
          <CardTitle>Résultats bruts</CardTitle>
          <CardDescription>Données brutes de l'évaluation</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <div className="space-y-6">
            {/* Légende */}
            <div className="flex flex-wrap gap-4">
              {(["A", "B", "C", "D"] as const).map((cat) => (
                <div key={cat} className="flex items-center gap-2 text-sm">
                  <span
                    className="inline-block h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: categorieColors[cat] }}
                  />
                  <span className="text-muted-foreground">
                    {categorieLabels[cat] ?? cat}
                  </span>
                </div>
              ))}
            </div>

            {/* Tables par famille */}
            {familles.map((famille) => (
              <div
                key={famille.nom}
                className="rounded-surface border overflow-hidden"
              >
                <table className="w-full text-sm">
                  <thead>
                    <tr
                      style={{
                        backgroundColor: `color-mix(in srgb, ${famille.color} 10%, transparent)`,
                      }}
                    >
                      <th
                        colSpan={2}
                        className="px-4 py-3 text-left font-bold"
                        style={{ color: famille.color }}
                      >
                        {famille.nom}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {famille.aptitudes.map((aptitude) => (
                      <tr
                        key={aptitude.nom}
                        className="border-t border-border hover:bg-muted/40 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-left w-2/3">
                          {aptitude.nom}
                        </td>
                        <td className="px-4 py-3 text-center w-1/3">
                          <span
                            className="inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-semibold text-surface w-full max-w-40"
                            style={{
                              backgroundColor:
                                categorieColors[aptitude.categorie] ??
                                "#6b7280",
                            }}
                          >
                            {categorieLabels[aptitude.categorie] ?? aptitude.categorie}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
